import { LogoPanel } from './LogoPanel.tsx';
import { useEffect, useState, type FormEvent } from 'react';
import { api, describeError, useOp } from '../../api.ts';
import { Link, useRouter } from '../../router.tsx';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { step, useKeys } from '../../ui/keys.ts';
import { Drawer, Empty, ErrorBox, Loading, Modal } from '../../ui/kit.tsx';
import { SearchBox } from '../../ui/SearchBox.tsx';
import { useToast } from '../../ui/toasts.tsx';
import { MerchantTabs } from './MerchantTabs.tsx';
import type { DomainRule, MasterDetail, MasterList, MergePreview, RuleTest } from './types.ts';
import { slugify } from './ui.tsx';

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'top_level', label: 'Top level' },
  { value: 'no_rules', label: 'No domain rule' },
  { value: 'inactive', label: 'Inactive' },
] as const;

/** Global (country-independent) merchants, their family tree and domain rules. */
export function MasterMerchants() {
  const { query, setQuery } = useRouter();
  const changes = useChanges();
  const q = query.get('q') ?? '';
  const filter = query.get('filter') ?? 'all';
  const open = query.get('master');
  const list = useOp<MasterList>('admin.masterMerchants.list', { q: q || undefined, filter, limit: 200 }, changes);
  const rows = list.data?.rows ?? [];
  const [cursor, setCursor] = useState(0);
  const [creating, setCreating] = useState(false);
  useEffect(() => setCursor(0), [q, filter]);

  useKeys(
    {
      j: () => setCursor((c) => step(c, 1, rows.length)),
      k: () => setCursor((c) => step(c, -1, rows.length)),
      enter: () => rows[cursor] && setQuery({ master: rows[cursor]!.ref }),
      o: () => rows[cursor] && setQuery({ master: rows[cursor]!.ref }),
      n: () => setCreating(true),
    },
    !open && !creating,
  );

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Merchants</h1>
          <MerchantTabs />
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
          New global merchant <kbd className="kbd">n</kbd>
        </button>
      </header>
      <DomainTester />
      <div className="toolbar">
        <SearchBox value={q} onChange={(value) => setQuery({ q: value || null }, { replace: true })} placeholder="Search name, slug or domain" />
        <div className="segmented" role="group" aria-label="Filter">
          {FILTERS.map((f) => (
            <button key={f.value} type="button" aria-pressed={filter === f.value} onClick={() => setQuery({ filter: f.value === 'all' ? null : f.value }, { replace: true })}>
              {f.label}
            </button>
          ))}
        </div>
        <span className="muted">{list.data ? `${list.data.total} global merchants` : ''}</span>
      </div>
      {list.error ? <ErrorBox error={list.error} onRetry={list.reload} /> : null}
      {!list.data && list.loading ? <Loading /> : null}
      {list.data && rows.length === 0 ? <Empty>No global merchants match.</Empty> : null}
      {rows.length ? (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Parent</th>
              <th scope="col">Countries</th>
              <th scope="col" className="num">Domain rules</th>
              <th scope="col" className="num">Children</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.ref} className={`clickable ${index === cursor ? 'cursor' : ''}`} onClick={() => setQuery({ master: row.ref })} onMouseEnter={() => setCursor(index)}>
                <td>
                  <button type="button" className="link-button strong" onClick={() => setQuery({ master: row.ref })}>
                    {row.name}
                  </button>
                  <small className="muted block">{row.slug}</small>
                  {row.status !== 'active' ? <span className="badge badge-muted">inactive</span> : null}
                </td>
                <td className="muted">{row.parent?.name ?? '–'}</td>
                <td>{row.markets.length ? row.markets.join(', ') : <span className="muted">not listed</span>}</td>
                <td className={`num ${row.domainRules === 0 ? 'text-warn' : ''}`}>{row.domainRules}</td>
                <td className="num">{row.children || '–'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {open ? <MasterDrawer masterRef={open} onClose={() => setQuery({ master: null })} /> : null}
      {creating ? <CreateMasterDrawer onClose={() => setCreating(false)} onCreated={(ref) => { setCreating(false); setQuery({ master: ref }); }} /> : null}
    </section>
  );
}

const RESULT_LABELS: Record<RuleTest['considered'][number]['result'], string> = {
  match: 'Wins',
  less_specific: 'Less specific',
  path_differs: 'Other path',
  other_market: 'Other market',
};

/** "Which merchant does this URL belong to?" */
export function DomainTester() {
  const [url, setUrl] = useState('');
  const [market, setMarket] = useState('');
  const [result, setResult] = useState<RuleTest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const test = async (event: FormEvent) => {
    event.preventDefault();
    try {
      setResult(await api.op<RuleTest>('admin.domainRules.test', { url, market: market || null }));
      setError(null);
    } catch (err) {
      setResult(null);
      setError(describeError(err));
    }
  };
  return (
    <form className="card domain-tester" onSubmit={test} aria-labelledby="tester-title">
      <h2 id="tester-title">Which merchant does this URL belong to?</h2>
      <div className="tester-row">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.sony.com/music/…" aria-label="URL to test" />
        <input value={market} onChange={(e) => setMarket(e.target.value.toUpperCase())} placeholder="GB" maxLength={2} className="input-narrow" aria-label="Market (optional)" />
        <button type="submit" className="btn" disabled={url.trim().length < 3}>
          Test
        </button>
      </div>
      {error ? <p className="field-error">{error}</p> : null}
      {result ? (
        <div className="tester-result" aria-live="polite">
          {result.match ? (
            <p>
              <strong>{result.match.name}</strong> via <code>{result.match.host}{result.match.pathPrefix === '/' ? '' : result.match.pathPrefix}</code>
              {result.match.market ? ` (${result.match.market} only)` : ''}
              {result.merchantInMarket ? (
                <>
                  {' · '}
                  <Link to={`/merchants/${result.merchantInMarket.ref}`}>open {result.merchantInMarket.name} in {market}</Link>
                </>
              ) : market ? (
                <span className="muted"> · not listed in {market}</span>
              ) : null}
            </p>
          ) : (
            <p className="text-warn">No merchant matches this URL.</p>
          )}
          {result.considered.length > 1 ? (
            <ul className="considered">
              {result.considered.map((c, i) => (
                <li key={i}>
                  <span className={`badge ${c.result === 'match' ? 'badge-ok' : 'badge-muted'}`}>{RESULT_LABELS[c.result]}</span> {c.name} <code>{c.host}{c.pathPrefix === '/' ? '' : c.pathPrefix}</code>
                  {c.market ? ` · ${c.market}` : ''}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </form>
  );
}

function CreateMasterDrawer({ onClose, onCreated }: { onClose: () => void; onCreated: (ref: string) => void }) {
  const toast = useToast();
  const [form, setForm] = useState({ name: '', slug: '', domain: '', homepageUrl: '' });
  const [slugTouched, setSlugTouched] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const result = await runWrite<{ ref: string }>(
      toast,
      'admin.masterMerchants.create',
      { name: form.name, slug: form.slug || slugify(form.name), homepageUrl: form.homepageUrl || null, domains: form.domain ? [form.domain] : [] },
      `${form.name} created`,
    );
    if (result) onCreated(result.ref);
  };
  return (
    <Drawer title="New global merchant" onClose={onClose}>
      <form onSubmit={submit}>
        <p className="muted small">A global merchant is the company itself. Add it to a country afterwards to give it coupons and pages.</p>
        <div className="form-grid">
          <label>
            Name
            <input value={form.name} autoFocus required onChange={(e) => setForm({ ...form, name: e.target.value, slug: slugTouched ? form.slug : slugify(e.target.value) })} />
          </label>
          <label>
            Slug
            <input value={form.slug} required onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: e.target.value }); }} />
          </label>
          <label>
            Main domain
            <input value={form.domain} placeholder="nike.com" onChange={(e) => setForm({ ...form, domain: e.target.value })} />
          </label>
          <label>
            Homepage
            <input value={form.homepageUrl} type="url" placeholder="https://www.nike.com" onChange={(e) => setForm({ ...form, homepageUrl: e.target.value })} />
          </label>
        </div>
        <button type="submit" className="btn btn-primary">
          Create
        </button>
      </form>
    </Drawer>
  );
}

function MasterDrawer({ masterRef, onClose }: { masterRef: string; onClose: () => void }) {
  const changes = useChanges();
  const detail = useOp<MasterDetail>('admin.masterMerchants.get', { ref: masterRef }, changes);
  const data = detail.data;
  return (
    <Drawer title={data?.name ?? 'Global merchant'} onClose={onClose} wide>
      {detail.error ? <ErrorBox error={detail.error} onRetry={detail.reload} /> : null}
      {!data && detail.loading ? <Loading /> : null}
      {data ? (
        <div className="master-detail">
          {!data.canEdit ? <p className="muted small">Global merchants are shared by every organization; changing them needs an owner or admin role.</p> : null}
          <MasterForm detail={data} />
          <LogoPanel masterRef={data.ref} canEdit={data.canEdit} hasHomepage={Boolean(data.homepageUrl)} />
          <RulesEditor detail={data} />
          <section aria-labelledby="listed-title">
            <h3 id="listed-title" className="section-title">
              Listed in
            </h3>
            {data.markets.length ? (
              <table className="table compact">
                <tbody>
                  {data.markets.map((m) => (
                    <tr key={m.ref}>
                      <th scope="row">{m.market}</th>
                      <td>
                        <Link to={`/merchants/${m.ref}`}>{m.localName}</Link> <span className="muted">/{m.slug}</span>
                      </td>
                      <td className="num">{m.coupons} coupons</td>
                      <td>{m.status !== 'active' ? <span className="badge badge-muted">inactive</span> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="muted">Not listed in any country yet.</p>
            )}
            <p className="muted small">
              {data.children.length ? `Children: ${data.children.map((c) => c.name).join(', ')} · ` : ''}
              {data.programs !== null ? `${data.programs} affiliate program${data.programs === 1 ? '' : 's'}` : ''}
            </p>
          </section>
          {data.canEdit ? <MergeSection detail={data} onMerged={onClose} /> : null}
        </div>
      ) : null}
    </Drawer>
  );
}

function MasterForm({ detail }: { detail: MasterDetail }) {
  const toast = useToast();
  const initial = () => ({
    name: detail.name,
    slug: detail.slug,
    parentRef: detail.parent?.ref ?? '',
    homepageUrl: detail.homepageUrl ?? '',
    wikidataQid: detail.wikidataQid ?? '',
    status: detail.status,
  });
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial()), [detail]);
  const parents = useOp<MasterList>('admin.masterMerchants.list', { filter: 'all', limit: 200 });
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    await runWrite<{ changed: string[] }>(toast, 'admin.masterMerchants.update', { ref: detail.ref, ...form, parentRef: form.parentRef || null }, (r) => (r.changed.length ? `Saved ${r.changed.join(', ')}` : 'Nothing changed'));
  };
  const disabled = !detail.canEdit;
  return (
    <form onSubmit={submit} aria-label="Identity">
      <div className="form-grid">
        <label>
          Name
          <input value={form.name} disabled={disabled} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label>
          Slug
          <input value={form.slug} disabled={disabled} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
        </label>
        <label>
          Parent
          <select value={form.parentRef} disabled={disabled} onChange={(e) => setForm({ ...form, parentRef: e.target.value })}>
            <option value="">None (top level)</option>
            {(parents.data?.rows ?? [])
              .filter((p) => p.ref !== detail.ref)
              .map((p) => (
                <option key={p.ref} value={p.ref}>
                  {p.name}
                </option>
              ))}
          </select>
        </label>
        <label>
          Status
          <select value={form.status} disabled={disabled} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
        <label>
          Homepage
          <input value={form.homepageUrl} type="url" disabled={disabled} onChange={(e) => setForm({ ...form, homepageUrl: e.target.value })} />
        </label>
        <label>
          Wikidata id
          <input value={form.wikidataQid} placeholder="Q12345" disabled={disabled} onChange={(e) => setForm({ ...form, wikidataQid: e.target.value })} />
        </label>
      </div>
      {Object.keys(detail.externalIds ?? {}).length ? (
        <p className="muted small" data-external-ids>
          Provider ids (set by program sync):{' '}
          {Object.entries(detail.externalIds)
            .map(([provider, id]) => `${provider} ${id}`)
            .join(', ')}
        </p>
      ) : null}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={disabled}>
          Save
        </button>
      </div>
    </form>
  );
}

function RulesEditor({ detail }: { detail: MasterDetail }) {
  const toast = useToast();
  const [draft, setDraft] = useState({ host: '', pathPrefix: '', market: '' });
  const add = async (event: FormEvent) => {
    event.preventDefault();
    const ok = await runWrite(toast, 'admin.domainRules.add', { masterRef: detail.ref, host: draft.host, pathPrefix: draft.pathPrefix || '/', market: draft.market || null }, 'Domain rule added');
    if (ok) setDraft({ host: '', pathPrefix: '', market: '' });
  };
  const remove = (rule: DomainRule) => runWrite(toast, 'admin.domainRules.delete', { id: rule.id }, `${rule.host} removed`);
  return (
    <section aria-labelledby="rules-title">
      <h3 id="rules-title" className="section-title">
        Domain rules
      </h3>
      <p className="muted small">The most specific rule wins: longer host, then longer path, then a market-specific rule.</p>
      <table className="table compact">
        <thead>
          <tr>
            <th scope="col">Host</th>
            <th scope="col">Path</th>
            <th scope="col">Market</th>
            <th scope="col" />
          </tr>
        </thead>
        <tbody>
          {detail.rules.map((rule) => (
            <RuleRow key={rule.id} rule={rule} canEdit={detail.canEdit} onRemove={() => remove(rule)} />
          ))}
          {detail.rules.length === 0 ? (
            <tr>
              <td colSpan={4} className="muted">
                No rules: links to this merchant’s site will not be recognised.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {detail.canEdit ? (
        <form className="rule-add" onSubmit={add} aria-label="Add domain rule">
          <input value={draft.host} placeholder="host, e.g. sony.com" aria-label="Host" onChange={(e) => setDraft({ ...draft, host: e.target.value })} required />
          <input value={draft.pathPrefix} placeholder="/path (optional)" aria-label="Path prefix" onChange={(e) => setDraft({ ...draft, pathPrefix: e.target.value })} />
          <input value={draft.market} placeholder="GB" aria-label="Market (optional)" maxLength={2} className="input-narrow" onChange={(e) => setDraft({ ...draft, market: e.target.value.toUpperCase() })} />
          <button type="submit" className="btn">
            Add rule
          </button>
        </form>
      ) : null}
    </section>
  );
}

function RuleRow({ rule, canEdit, onRemove }: { rule: DomainRule; canEdit: boolean; onRemove: () => void }) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ host: rule.host, pathPrefix: rule.pathPrefix, market: rule.market ?? '' });
  if (editing) {
    const save = async () => {
      const ok = await runWrite(toast, 'admin.domainRules.update', { id: rule.id, host: draft.host, pathPrefix: draft.pathPrefix, market: draft.market || null }, 'Rule saved');
      if (ok) setEditing(false);
    };
    return (
      <tr>
        <td>
          <input value={draft.host} aria-label="Host" onChange={(e) => setDraft({ ...draft, host: e.target.value })} />
        </td>
        <td>
          <input value={draft.pathPrefix} aria-label="Path prefix" onChange={(e) => setDraft({ ...draft, pathPrefix: e.target.value })} />
        </td>
        <td>
          <input value={draft.market} aria-label="Market" className="input-narrow" maxLength={2} onChange={(e) => setDraft({ ...draft, market: e.target.value.toUpperCase() })} />
        </td>
        <td className="actions">
          <button type="button" className="btn btn-small btn-primary" onClick={save}>
            Save
          </button>
          <button type="button" className="btn btn-small btn-ghost" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td>
        <code>{rule.host}</code>
      </td>
      <td>{rule.pathPrefix === '/' ? <span className="muted">any</span> : <code>{rule.pathPrefix}</code>}</td>
      <td>{rule.market ?? <span className="muted">all</span>}</td>
      <td className="actions">
        {canEdit ? (
          <>
            <button type="button" className="btn btn-small btn-ghost" onClick={() => setEditing(true)}>
              Edit
            </button>
            <button type="button" className="btn btn-small btn-ghost btn-danger-ghost" onClick={onRemove} aria-label={`Remove ${rule.host}`}>
              Remove
            </button>
          </>
        ) : null}
      </td>
    </tr>
  );
}

/** This merchant is a duplicate → merge it into another one: preview first, one confirm. */
function MergeSection({ detail, onMerged }: { detail: MasterDetail; onMerged: () => void }) {
  const toast = useToast();
  const [q, setQ] = useState('');
  const candidates = useOp<MasterList>('admin.masterMerchants.list', { q: q || undefined, limit: 20 }, 0, q.trim().length >= 2);
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);

  const plan = async (targetRef: string) => {
    try {
      setPreview(await api.op<MergePreview>('admin.masterMerchants.previewMerge', { sourceRef: detail.ref, targetRef }));
      setError(null);
    } catch (err) {
      setError(describeError(err));
    }
  };
  const merge = async () => {
    if (!preview) return;
    setMerging(true);
    const ok = await runWrite(toast, 'admin.masterMerchants.merge', { sourceRef: detail.ref, targetRef: preview.target.ref, confirm: true }, `${detail.name} merged into ${preview.target.name}`);
    setMerging(false);
    if (ok) onMerged();
  };

  return (
    <details className="merge-section">
      <summary>This is a duplicate: merge into another merchant</summary>
      <label>
        Merge into
        <input value={q} placeholder="Search the merchant to keep" onChange={(e) => setQ(e.target.value)} />
      </label>
      {q.trim().length >= 2 ? (
        <ul className="pick-list">
          {(candidates.data?.rows ?? [])
            .filter((c) => c.ref !== detail.ref)
            .map((c) => (
              <li key={c.ref}>
                <button type="button" className="row-button" onClick={() => plan(c.ref)}>
                  <span className="strong">{c.name}</span>
                  <small className="muted">
                    {c.slug} · {c.markets.join(', ') || 'not listed'}
                  </small>
                </button>
              </li>
            ))}
        </ul>
      ) : null}
      {error ? <p className="field-error">{error}</p> : null}
      {preview ? (
        <Modal
          title={`Merge ${preview.source.name} into ${preview.target.name}?`}
          onClose={() => setPreview(null)}
          actions={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setPreview(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={merge} disabled={merging}>
                {merging ? 'Merging…' : 'Merge'}
              </button>
            </>
          }
        >
          <ul className="summary-chips">
            <li>{preview.domainRules} domain rules move</li>
            <li>{preview.children} child merchants move</li>
            {preview.programs !== null ? <li>{preview.programs} affiliate programs move</li> : null}
            {preview.marketsMoved.length ? <li>Countries move: {preview.marketsMoved.join(', ')}</li> : null}
            {preview.marketsCombined.map((m) => (
              <li key={m.market} className="warn">
                {m.market}: {m.coupons} coupons join {preview.target.name}
              </li>
            ))}
          </ul>
          {preview.warnings.map((w) => (
            <p key={w} className="text-warn small">
              {w}
            </p>
          ))}
          <p className="muted small">{preview.source.name} stays as an inactive record so old links still resolve.</p>
        </Modal>
      ) : null}
    </details>
  );
}
